import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const YourPay = () => (
  <Page
    title="Your Pay"
    intro="My Pay shows everything about your money: paid statements,
    pending pay for shifts you've worked but not yet been paid for, overtime,
    expenses — and, for invoice workers, generating or uploading your pay
    invoices."
  >
    <Section title="The My Pay page">
      <p>
        <DocLink text="My Pay" url="/my-pay" /> opens with summary tiles for
        your paid and pending totals, followed by your statements. What you
        see depends on how you're paid:
      </p>
      <Bullets>
        <Bullet>
          <strong>PAYE-style staff</strong> see <strong>Pay Statements</strong>{' '}
          — one per pay run you were paid in, each listing your shifts with
          hours and pay, downloadable for your own records.
        </Bullet>
        <Bullet>
          <strong>Invoice workers</strong> see{' '}
          <strong>Remittance Summaries</strong> instead, plus the invoice
          tools covered below.
        </Bullet>
      </Bullets>
      <Screenshot url="workforce/my-pay.png">
        My Pay with the summary tiles, pending pay, and your statements.
      </Screenshot>
      <Info title="Pending Pay is an estimate">
        The <strong>Pending Pay</strong> table lists completed shifts that
        haven't been paid yet, with a per-shift breakdown — base pay,
        approved overtime, fixed bonuses, and paid travel each in their own
        column. It is an estimate until payroll runs, and stays separate from
        your paid statements.
      </Info>
    </Section>

    <Section title="Overtime claims">
      <p>
        You don't fill in an overtime form. When you clock out after your
        planned finish (past the organisation's grace window), AmbuNet asks
        why and records an overtime claim automatically, rounded to your
        organisation's rule. The claim starts as <strong>Pending</strong>;
        your managers then approve or reject it with the rate and minutes
        that will be paid. On My Pay, pending overtime is highlighted and
        only approved overtime counts towards your pay.
      </p>
      <Warning title="A claim belongs to you, not the shift">
        An overtime claim is dropped if the shift is reassigned to someone
        else — it cannot follow the shift to the new assignee. If a shift of
        yours with an overtime claim is being swapped or reassigned, make
        sure the claim has been dealt with first.
      </Warning>
    </Section>

    <Section title="Expense claims">
      <p>
        Log an expense from My Pay with a date, description, category
        (Travel, Mileage, Accommodation, Equipment, Subsistence, or Other),
        the amount, an optional link to a recent shift, and a receipt. Each
        claim shows as <strong>Pending</strong>, <strong>Approved</strong>, or{' '}
        <strong>Rejected</strong>; approved expenses are added to a pay run
        as their own lines, so they are paid alongside your wages and appear
        on your statement.
      </p>
    </Section>

    <Section title="Invoice workers — generating and uploading invoices">
      <p>
        If you're paid against invoices, My Pay has two buttons:
      </p>
      <Bullets>
        <Bullet>
          <strong>Generate Invoice</strong> — pick the completed, verified
          unpaid shifts (and approved overtime) to cover, and AmbuNet creates
          a self-billing invoice PDF with the correct lines and totals, then
          submits it for you.
        </Bullet>
        <Bullet>
          <strong>Upload Invoice</strong> — submit your own invoice instead:
          pick the shifts it covers, enter your invoice number, date, and
          amount, and attach the document.
        </Bullet>
      </Bullets>
      <Screenshot url="workforce/my-pay-upload-invoice.png">
        The invoice upload dialog — pick the shifts the invoice covers and
        enter the exact amount.
      </Screenshot>
      <Warning title="The amount must match exactly">
        An uploaded invoice must match the expected total for the selected
        shifts to the penny, or it is refused. If you have a VAT number
        recorded on your payroll profile, you invoice your pay{' '}
        <strong>plus standard-rate VAT</strong> — the dialog shows the net,
        VAT, and gross figures, and the gross is the amount to invoice.
      </Warning>
      <p>
        Your invoices are listed with a status: <strong>Submitted</strong>{' '}
        while payroll reviews it, then <strong>Approved</strong> (and later{' '}
        <strong>Matched</strong> to a pay run), <strong>Queried</strong>, or{' '}
        <strong>Rejected</strong>. A queried invoice means payroll has a
        question about it — expect them to contact you, since the pay run
        covering those shifts cannot be completed until the query is
        resolved. A rejected invoice frees the shifts so you can submit a
        corrected one.
      </p>
    </Section>

    <Section title="What Verified and Paid mean">
      <Bullets>
        <Bullet>
          <strong>Verified</strong> — the shift is confirmed as worked.
          Clocking out cleanly on time verifies it automatically; otherwise a
          manager verifies it. Only verified shifts can be paid or invoiced.
        </Bullet>
        <Bullet>
          <strong>Paid</strong> — the shift was included in a pay run that
          has been marked paid. That's the moment it leaves Pending Pay and
          its statement appears on My Pay.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Any signed-in staff member sees their own My Pay. The Generate
        Invoice and Upload Invoice buttons only appear for workers whose
        payroll profile is set to the invoice employment type. Running
        payroll? See{' '}
        <DocLink
          docsPage
          url="/employees-hr/rostering-payroll"
          text="Rostering, Availability, and Payroll"
        />
        .
      </Info>
    </Section>
  </Page>
)

export default YourPay
